import type { DeliveryEntry } from "../orders/order-delivery-schedule";
import { CHANGE_HISTORY_FIELDS, CHANGE_HISTORY_TYPES } from "./audit-history";
import type { OrderStatus } from "../orders/order-types";

export const DEMO_BATCH_PREFIX = "demo";

export const DEMO_CONTACT_PERSON = "Demo Contact";
export const DEMO_CONTACT_PHONES = { primary: "555-0100", secondary: "555-0110" } as const;
function buildDemoBatchNumber(productNumber: string, controlNumber: number): string {
  return `${DEMO_BATCH_PREFIX}-${productNumber}-${String(controlNumber).padStart(4, "0")}`;
}

export const DEMO_CHEMICAL_DEFINITIONS = {
  buffer: {
    productNumber: "DEMO-BUF-001",
    name: "Phosphate Buffer",
    casNumber: "7558-79-4",
    batchNumber: buildDemoBatchNumber("DEMO-BUF-001", 1),
    location: "Demo Shelf A",
    amount: 5,
    unit: "l",
    costPerUnit: "42.00",
  },
  ethanol: {
    productNumber: "DEMO-ETH-001",
    name: "Ethanol 95%",
    casNumber: "64-17-5",
    batchNumber: buildDemoBatchNumber("DEMO-ETH-001", 1),
    location: "Demo Flammables Cabinet",
    amount: 12,
    unit: "l",
    costPerUnit: "18.50",
  },
  acetone: {
    productNumber: "DEMO-ACE-001",
    name: "Acetone",
    casNumber: "67-64-1",
    batchNumber: buildDemoBatchNumber("DEMO-ACE-001", 1),
    location: "Demo Flammables Cabinet",
    amount: 8,
    unit: "l",
    costPerUnit: "15.75",
  },
  methanol: {
    productNumber: "DEMO-MEO-001",
    name: "Methanol",
    casNumber: "67-56-1",
    batchNumber: buildDemoBatchNumber("DEMO-MEO-001", 1),
    location: "Demo Flammables Cabinet",
    amount: 10,
    unit: "l",
    costPerUnit: "12.30",
  },
  isopropanol: {
    productNumber: "DEMO-IPA-001",
    name: "Isopropyl Alcohol 99%",
    casNumber: "67-63-0",
    batchNumber: buildDemoBatchNumber("DEMO-IPA-001", 1),
    location: "Demo Shelf B",
    amount: 20,
    unit: "l",
    costPerUnit: "9.40",
  },
  sodiumHydroxide: {
    productNumber: "DEMO-NAOH-001",
    name: "Sodium Hydroxide Pellets",
    casNumber: "1310-73-2",
    batchNumber: buildDemoBatchNumber("DEMO-NAOH-001", 1),
    location: "Demo Corrosives Cabinet",
    amount: 25,
    unit: "kg",
    costPerUnit: "6.20",
  },
  hydrochloricAcid: {
    productNumber: "DEMO-HCL-001",
    name: "Hydrochloric Acid 37%",
    casNumber: "7647-01-0",
    batchNumber: buildDemoBatchNumber("DEMO-HCL-001", 1),
    location: "Demo Corrosives Cabinet",
    amount: 6,
    unit: "l",
    costPerUnit: "22.10",
  },
  sulfuricAcid: {
    productNumber: "DEMO-H2SO4-001",
    name: "Sulfuric Acid 98%",
    casNumber: "7664-93-9",
    batchNumber: buildDemoBatchNumber("DEMO-H2SO4-001", 1),
    location: "Demo Corrosives Cabinet",
    amount: 4,
    unit: "l",
    costPerUnit: "27.85",
  },
  sodiumChloride: {
    productNumber: "DEMO-NACL-001",
    name: "Sodium Chloride",
    casNumber: "7647-14-5",
    batchNumber: buildDemoBatchNumber("DEMO-NACL-001", 1),
    location: "Demo Shelf A",
    amount: 15,
    unit: "kg",
    costPerUnit: "3.50",
  },
  potassiumPermanganate: {
    productNumber: "DEMO-KMNO4-001",
    name: "Potassium Permanganate",
    casNumber: "7722-64-7",
    batchNumber: buildDemoBatchNumber("DEMO-KMNO4-001", 1),
    location: "Demo Shelf C",
    amount: 3,
    unit: "kg",
    costPerUnit: "31.00",
  },
} as const;

export type DemoChemicalKey = keyof typeof DEMO_CHEMICAL_DEFINITIONS;

export const DEMO_USER_DEFINITIONS = {
  admin: { id: 1, email: "demo.admin@example.com", name: "Demo Admin", role: "admin" as const },
  manager: { id: 2, email: "demo.manager@example.com", name: "Demo Manager", role: "manager" as const },
  chemist: { id: 3, email: "demo.chemist@example.com", name: "Demo Chemist", role: "chemist" as const },
} as const;

export type DemoUserKey = keyof typeof DEMO_USER_DEFINITIONS;

export const DEMO_ADDITIONAL_TENANT_USERS = [
  { id: 4, email: "j.rivera@example.com", name: "Jordan Rivera", role: "chemist" as const },
  { id: 5, email: "s.patel@example.com", name: "Sana Patel", role: "chemist" as const },
  { id: 6, email: "m.chen@example.com", name: "Marcus Chen", role: "manager" as const },
  { id: 7, email: "a.osei@example.com", name: "Ama Osei", role: "chemist" as const },
  { id: 8, email: "l.nguyen@example.com", name: "Linh Nguyen", role: "chemist" as const },
  { id: 9, email: "d.kowalski@example.com", name: "Dana Kowalski", role: "manager" as const },
  { id: 10, email: "r.fischer@example.com", name: "Ravi Fischer", role: "chemist" as const },
] as const;

export const DEMO_SUPPLIER_DEFINITION = {
  name: "Northstar Lab Supply",
  email: "orders@northstar.example",
} as const;

export const DEMO_PURCHASER_DEFINITION = {
  name: "Acme Research Group",
  email: "receiving@acme-research.example",
} as const;

export const DEMO_CONTACT_DEFINITIONS = [
  { id: 1, key: "northstar", company: DEMO_SUPPLIER_DEFINITION.name, email: DEMO_SUPPLIER_DEFINITION.email, contactType: "supplier" as const },
  { id: 2, key: "acmeResearch", company: DEMO_PURCHASER_DEFINITION.name, email: DEMO_PURCHASER_DEFINITION.email, contactType: "purchaser" as const },
  { id: 3, key: "brightChem", company: "Bright Chem Distributors", email: "sales@brightchem.example", contactType: "supplier" as const },
  { id: 4, key: "veritasLabs", company: "Veritas Labs", email: "procurement@veritaslabs.example", contactType: "purchaser" as const },
  { id: 5, key: "summitReagents", company: "Summit Reagents Co", email: "info@summitreagents.example", contactType: "supplier" as const },
  { id: 6, key: "pioneerBiotech", company: "Pioneer Biotech", email: "orders@pioneerbiotech.example", contactType: "purchaser" as const },
  { id: 7, key: "clearwaterScientific", company: "Clearwater Scientific", email: "supply@clearwaterscientific.example", contactType: "supplier" as const },
  { id: 8, key: "meridianDiagnostics", company: "Meridian Diagnostics", email: "purchasing@meridiandx.example", contactType: "purchaser" as const },
  { id: 9, key: "atlasChemicalWorks", company: "Atlas Chemical Works", email: "sales@atlaschem.example", contactType: "supplier_and_purchaser" as const },
  { id: 10, key: "harborResearch", company: "Harbor Research Institute", email: "labops@harborresearch.example", contactType: "purchaser" as const },
] as const;

export type DemoContactKey = (typeof DEMO_CONTACT_DEFINITIONS)[number]["key"];

export type DemoOrderDefinition = {
  chemical: DemoChemicalKey;
  contact: DemoContactKey;
  requestedBy: DemoUserKey;
  direction: "purchase" | "sale";
  orderStatus: OrderStatus;
  quantity: number;
  unit: string;
  pricePerItem: string;
  externalProductNumber: string;
  internalBatchNumber: string;
  referenceNumber: string;
  submittedAt: string;
  deliverySchedule?: DeliveryEntry[];
  orderedAt?: string;
  arrivedDate?: string;
  externalBatchNumber?: string;
  cancellationReason?: string;
  notes: string;
};

export const DEMO_ORDER_DEFINITIONS = [
  { chemical: "buffer", contact: "northstar", requestedBy: "chemist", direction: "purchase", orderStatus: "requested", quantity: 8, unit: "l", pricePerItem: "44.00", externalProductNumber: "BUF-7558-8L", internalBatchNumber: "DEMO-BUF-001-0002", referenceNumber: "DEMO-ORDER-1001", submittedAt: "2026-07-08T13:30:00.000Z", deliverySchedule: [{ date: "2026-07-29", quantity: 8 }], notes: "Requested for the next formulation campaign." },
  { chemical: "ethanol", contact: "brightChem", requestedBy: "manager", direction: "purchase", orderStatus: "requested", quantity: 24, unit: "l", pricePerItem: "19.25", externalProductNumber: "ETH-95-24L", internalBatchNumber: "DEMO-ETH-001-0002", referenceNumber: "DEMO-ORDER-1002", submittedAt: "2026-07-10T15:00:00.000Z", deliverySchedule: [{ date: "2026-08-01", quantity: 24 }], notes: "Quarterly solvent restock." },
  { chemical: "acetone", contact: "summitReagents", requestedBy: "admin", direction: "purchase", orderStatus: "ordered", quantity: 12, unit: "l", pricePerItem: "16.10", externalProductNumber: "ACE-HPLC-12L", internalBatchNumber: "DEMO-ACE-001-0002", referenceNumber: "DEMO-ORDER-1003", submittedAt: "2026-07-03T14:00:00.000Z", deliverySchedule: [{ date: "2026-07-28", quantity: 6 }, { date: "2026-08-04", quantity: 6 }], orderedAt: "2026-07-04T14:30:00.000Z", notes: "Vendor confirmed shipment." },
  { chemical: "methanol", contact: "clearwaterScientific", requestedBy: "chemist", direction: "purchase", orderStatus: "ordered", quantity: 10, unit: "l", pricePerItem: "13.05", externalProductNumber: "MEO-ACS-10L", internalBatchNumber: "DEMO-MEO-001-0002", referenceNumber: "DEMO-ORDER-1004", submittedAt: "2026-07-05T16:15:00.000Z", deliverySchedule: [{ date: "2026-07-30", quantity: 10 }], orderedAt: "2026-07-06T13:45:00.000Z", notes: "Expected with the weekly delivery." },
  { chemical: "isopropanol", contact: "northstar", requestedBy: "manager", direction: "purchase", orderStatus: "fulfilled", quantity: 20, unit: "l", pricePerItem: "9.75", externalProductNumber: "IPA-99-20L", internalBatchNumber: "DEMO-IPA-001-0002", externalBatchNumber: "NS-IPA-0627", referenceNumber: "DEMO-ORDER-1005", submittedAt: "2026-06-20T13:00:00.000Z", orderedAt: "2026-06-21T14:00:00.000Z", arrivedDate: "2026-06-27T16:00:00.000Z", notes: "Received complete and undamaged." },
  { chemical: "sodiumHydroxide", contact: "pioneerBiotech", requestedBy: "admin", direction: "sale", orderStatus: "fulfilled", quantity: 5, unit: "kg", pricePerItem: "8.40", externalProductNumber: "NAOH-PEL-5K", internalBatchNumber: "DEMO-NAOH-001-0002", externalBatchNumber: "PB-NAOH-625", referenceNumber: "DEMO-ORDER-1006", submittedAt: "2026-06-22T15:00:00.000Z", orderedAt: "2026-06-23T13:30:00.000Z", arrivedDate: "2026-06-25T17:00:00.000Z", notes: "Outbound order shipped by ground." },
  { chemical: "hydrochloricAcid", contact: "veritasLabs", requestedBy: "chemist", direction: "sale", orderStatus: "fulfilled", quantity: 2, unit: "l", pricePerItem: "31.00", externalProductNumber: "HCL-37-2L", internalBatchNumber: "DEMO-HCL-001-0002", externalBatchNumber: "VL-HCL-630", referenceNumber: "DEMO-ORDER-1007", submittedAt: "2026-06-26T14:00:00.000Z", orderedAt: "2026-06-27T15:00:00.000Z", arrivedDate: "2026-06-30T16:30:00.000Z", notes: "Hazmat documentation included." },
  { chemical: "sulfuricAcid", contact: "atlasChemicalWorks", requestedBy: "manager", direction: "purchase", orderStatus: "cancelled", quantity: 4, unit: "l", pricePerItem: "29.50", externalProductNumber: "H2SO4-98-4L", internalBatchNumber: "DEMO-H2SO4-001-0002", referenceNumber: "DEMO-ORDER-1008", submittedAt: "2026-07-01T13:15:00.000Z", cancellationReason: "Duplicate request discovered during review.", notes: "Cancelled before placement." },
  { chemical: "sodiumChloride", contact: "meridianDiagnostics", requestedBy: "admin", direction: "sale", orderStatus: "cancelled", quantity: 10, unit: "kg", pricePerItem: "5.25", externalProductNumber: "NACL-ACS-10K", internalBatchNumber: "DEMO-NACL-001-0002", referenceNumber: "DEMO-ORDER-1009", submittedAt: "2026-06-29T14:45:00.000Z", orderedAt: "2026-06-30T13:00:00.000Z", cancellationReason: "Customer changed the required grade.", notes: "Cancelled after initiation." },
  { chemical: "potassiumPermanganate", contact: "harborResearch", requestedBy: "chemist", direction: "sale", orderStatus: "ordered", quantity: 1, unit: "kg", pricePerItem: "46.00", externalProductNumber: "KMNO4-ACS-1K", internalBatchNumber: "DEMO-KMNO4-001-0002", referenceNumber: "DEMO-ORDER-1010", submittedAt: "2026-07-07T15:30:00.000Z", deliverySchedule: [{ date: "2026-07-31", quantity: 1 }], orderedAt: "2026-07-08T14:15:00.000Z", notes: "Awaiting outbound carrier pickup." },
] as const satisfies readonly DemoOrderDefinition[];

export const DEMO_TRANSACTION_DEFINITIONS = [
  { chemical: "buffer" as DemoChemicalKey, transactionType: "PURCHASE", quantity: 5, unit: "l", contact: "northstar" as DemoContactKey, party: "supplier" as const, extras: { price: "210.00", referenceNumber: "DEMO-PO-1001", notes: "Demo opening stock" } },
  { chemical: "ethanol" as DemoChemicalKey, transactionType: "SALE", quantity: 2, unit: "l", contact: "acmeResearch" as DemoContactKey, party: "purchaser" as const, extras: { price: "74.00", referenceNumber: "DEMO-INV-2001", notes: "Demo outbound order" } },
  { chemical: "acetone" as DemoChemicalKey, transactionType: "PURCHASE", quantity: 8, unit: "l", contact: "brightChem" as DemoContactKey, party: "supplier" as const, extras: { price: "126.00", referenceNumber: "DEMO-PO-1002", notes: "Demo restock" } },
  { chemical: "methanol" as DemoChemicalKey, transactionType: "SALE", quantity: 3, unit: "l", contact: "veritasLabs" as DemoContactKey, party: "purchaser" as const, extras: { price: "36.90", referenceNumber: "DEMO-INV-2002", notes: "Demo outbound order" } },
  { chemical: "isopropanol" as DemoChemicalKey, transactionType: "PURCHASE", quantity: 20, unit: "l", contact: "summitReagents" as DemoContactKey, party: "supplier" as const, extras: { price: "188.00", referenceNumber: "DEMO-PO-1003", notes: "Demo restock" } },
  { chemical: "sodiumHydroxide" as DemoChemicalKey, transactionType: "SALE", quantity: 5, unit: "kg", contact: "pioneerBiotech" as DemoContactKey, party: "purchaser" as const, extras: { price: "31.00", referenceNumber: "DEMO-INV-2003", notes: "Demo outbound order" } },
  { chemical: "hydrochloricAcid" as DemoChemicalKey, transactionType: "PURCHASE", quantity: 6, unit: "l", contact: "clearwaterScientific" as DemoContactKey, party: "supplier" as const, extras: { price: "132.60", referenceNumber: "DEMO-PO-1004", notes: "Demo restock" } },
  { chemical: "sulfuricAcid" as DemoChemicalKey, transactionType: "USAGE", quantity: 1, unit: "l", contact: null, party: null, extras: { price: null, referenceNumber: null, notes: "Demo lab usage" } },
  { chemical: "sodiumChloride" as DemoChemicalKey, transactionType: "SALE", quantity: 4, unit: "kg", contact: "meridianDiagnostics" as DemoContactKey, party: "purchaser" as const, extras: { price: "14.00", referenceNumber: "DEMO-INV-2004", notes: "Demo outbound order" } },
  { chemical: "potassiumPermanganate" as DemoChemicalKey, transactionType: "DISPOSAL", quantity: 0.5, unit: "kg", contact: null, party: null, extras: { price: null, referenceNumber: null, notes: "Demo expired stock disposal" } },
] as const;

export const DEMO_CHANGE_HISTORY_DEFINITIONS = [
  { id: 1, chemical: "buffer" as DemoChemicalKey, user: "admin" as DemoUserKey, changeType: CHANGE_HISTORY_TYPES.CREATED, fieldName: null, oldValue: null, newValue: null },
  { id: 2, chemical: "ethanol" as DemoChemicalKey, user: "admin" as DemoUserKey, changeType: CHANGE_HISTORY_TYPES.CREATED, fieldName: null, oldValue: null, newValue: null },
  { id: 3, chemical: "acetone" as DemoChemicalKey, user: "manager" as DemoUserKey, changeType: CHANGE_HISTORY_TYPES.CREATED, fieldName: null, oldValue: null, newValue: null },
  { id: 4, chemical: "methanol" as DemoChemicalKey, user: "chemist" as DemoUserKey, changeType: CHANGE_HISTORY_TYPES.UPDATED, fieldName: CHANGE_HISTORY_FIELDS.amount, oldValue: "14", newValue: "10" },
  { id: 5, chemical: "isopropanol" as DemoChemicalKey, user: "admin" as DemoUserKey, changeType: CHANGE_HISTORY_TYPES.CREATED, fieldName: null, oldValue: null, newValue: null },
  { id: 6, chemical: "sodiumHydroxide" as DemoChemicalKey, user: "manager" as DemoUserKey, changeType: CHANGE_HISTORY_TYPES.UPDATED, fieldName: "location", oldValue: "Demo Shelf B", newValue: "Demo Corrosives Cabinet" },
  { id: 7, chemical: "hydrochloricAcid" as DemoChemicalKey, user: "chemist" as DemoUserKey, changeType: CHANGE_HISTORY_TYPES.UPDATED, fieldName: CHANGE_HISTORY_FIELDS.amount, oldValue: "7", newValue: "6" },
  { id: 8, chemical: "sulfuricAcid" as DemoChemicalKey, user: "chemist" as DemoUserKey, changeType: CHANGE_HISTORY_TYPES.UPDATED, fieldName: CHANGE_HISTORY_FIELDS.amount, oldValue: "5", newValue: "4" },
  { id: 9, chemical: "sodiumChloride" as DemoChemicalKey, user: "admin" as DemoUserKey, changeType: CHANGE_HISTORY_TYPES.CREATED, fieldName: null, oldValue: null, newValue: null },
  { id: 10, chemical: "potassiumPermanganate" as DemoChemicalKey, user: "manager" as DemoUserKey, changeType: CHANGE_HISTORY_TYPES.DELETED, fieldName: null, oldValue: null, newValue: null },
] as const;
